/**
 * Centralized date utilities. All date handling must use these — never
 * `new Date()`, `Date.now()`, native Date math, or a direct `dayjs`
 * import outside this package.
 */

import { dayjs } from "./dayjs";
import type { DateInput } from "./types";

// ── Duration constants (ms) — the single source for `n * day/hour/minute` math ─

/** One second in milliseconds. */
export const SECOND_MS = 1000;
/** One minute in milliseconds. */
export const MINUTE_MS = 60 * SECOND_MS;
/** One hour in milliseconds. */
export const HOUR_MS = 60 * MINUTE_MS;
/** One day in milliseconds. */
export const DAY_MS = 24 * HOUR_MS;

// ── Current time (the only sanctioned source of "now") ──────────────────────

export function getCurrentDate(): Date {
	return dayjs().toDate();
}
export function getCurrentDateISO(): string {
	return dayjs().toISOString();
}
export function getCurrentTimestampMs(): number {
	return dayjs().valueOf();
}
export function getCurrentUnixSeconds(): number {
	return dayjs().unix();
}
export function toTimestampMs(date: DateInput): number {
	return dayjs(date).valueOf();
}

/**
 * Parse a string with the runtime's `Date.parse` semantics. Compatibility-
 * only: some contracts accept the runtime's broad parseable set and rely
 * on its UTC treatment of date-only strings; new contracts should prefer explicit format validation.
 */
export function parseDateStringToTimestampMs(value: string): number {
	return Date.parse(value);
}

// ── Parsing ─────────────────────────────────────────────────────────────────

export function parseToDate(value: DateInput): Date | null {
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.toDate() : null;
}
export function parseToDateStrict(value: DateInput): Date {
	const parsed = dayjs(value);
	if (!parsed.isValid()) {
		throw new Error(`Invalid date value: ${String(value)}`);
	}
	return parsed.toDate();
}

/** Strict UTC `YYYY-MM-DD` calendar-date parser. Rejects rollover dates like `2026-02-31`. */
export function parseUtcDateOnly(value: string): Date | null {
	const parsed = dayjs.utc(value, "YYYY-MM-DD", true);
	return parsed.isValid() ? parsed.toDate() : null;
}

export function parseUtcDateOnlyStrict(value: string): Date {
	const parsed = parseUtcDateOnly(value);
	if (!parsed) {
		throw new Error(`Invalid UTC date-only value: ${value}`);
	}
	return parsed;
}

export function isUtcDateOnly(value: string): boolean {
	return parseUtcDateOnly(value) !== null;
}
export function isDateValid(value: DateInput): boolean {
	return dayjs(value).isValid();
}

// ── Arithmetic ────────────────────────────────────────────────────────────────

export function addMinutes(date: DateInput, minutes: number): Date {
	return dayjs(date).add(minutes, "minute").toDate();
}
export function addHours(date: DateInput, hours: number): Date {
	return dayjs(date).add(hours, "hour").toDate();
}
export function addDays(date: DateInput, days: number): Date {
	return dayjs(date).add(days, "day").toDate();
}
export function addMilliseconds(date: DateInput, milliseconds: number): Date {
	return dayjs(date).add(milliseconds, "millisecond").toDate();
}
export function subtractDays(date: DateInput, days: number): Date {
	return dayjs(date).subtract(days, "day").toDate();
}
export function subtractMilliseconds(date: DateInput, milliseconds: number): Date {
	return dayjs(date).subtract(milliseconds, "millisecond").toDate();
}
export function addMonths(date: DateInput, months: number): Date {
	return dayjs(date).add(months, "month").toDate();
}

// ── UTC calendar-day bucketing ────────────────────────────────────────────────
// Trend/analytics bucket on UTC calendar days. The native UTC math lives HERE
// (the sanctioned dates package) so consumers never reach for `Date.UTC` /
// `new Date(ms)` / `.toISOString().slice()` themselves.

/** The UTC calendar-day key (`YYYY-MM-DD`) for an instant. */
export function utcDayKey(value: DateInput): string {
	return dayjs(value).toISOString().slice(0, 10);
}

/** Midnight-UTC of `value`'s calendar day, in epoch milliseconds. */
function startOfUtcDayMs(value: DateInput) {
	const date = dayjs(value).toDate();
	return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** Midnight-UTC of `value`'s calendar day. */
export function startOfUtcDay(value: DateInput): Date {
	return new Date(startOfUtcDayMs(value));
}

/**
 * A contiguous window of `days` UTC calendar days ending on `end`'s UTC day
 * (inclusive), oldest first: the earliest instant to query (`start`, midnight
 * UTC of the first day) plus the ordered `YYYY-MM-DD` day keys for zero-filling.
 */
export function utcDayRange(end: DateInput, days: number): { start: Date; keys: string[] } {
	const endDayMs = startOfUtcDayMs(end);
	const start = new Date(endDayMs - (days - 1) * DAY_MS);
	const keys = Array.from({ length: days }, (_, index) => utcDayKey(new Date(endDayMs - (days - 1 - index) * DAY_MS)));
	return { start, keys };
}

// ── Comparisons ───────────────────────────────────────────────────────────────

export function isDateBefore(a: DateInput, b: DateInput): boolean {
	return dayjs(a).isBefore(dayjs(b));
}
export function isDateAfter(a: DateInput, b: DateInput): boolean {
	return dayjs(a).isAfter(dayjs(b));
}
/** The latest of a set of instants (max), or null for an empty set. Returns a real `Date`. */
export function latestDate(dates: DateInput[]): Date | null {
	if (dates.length === 0) {
		return null;
	}
	return dates
		.map((date) => dayjs(date))
		.reduce((latest, current) => (current.isAfter(latest) ? current : latest))
		.toDate();
}
export function isDateSameOrBefore(a: DateInput, b: DateInput): boolean {
	const x = dayjs(a);
	const y = dayjs(b);
	return x.isSame(y) || x.isBefore(y);
}
export function isUtcDateOnlySameOrBefore(a: string, b: DateInput): boolean {
	const parsed = parseUtcDateOnly(a);
	if (!parsed) {
		return false;
	}
	return isDateSameOrBefore(parsed, b);
}
export function isDateWithinRange(date: DateInput, start: DateInput, end: DateInput): boolean {
	return isDateSameOrBefore(start, date) && isDateSameOrBefore(date, end);
}

// ── Difference & freshness ──────────────────────────────────────────────────
// Freshness backs the Device Passport's per-assessment freshness check: the
// passport never expires, but a consumer (program partner) supplies a max-age window per
// assessment ([03 core-domain] / [05 device-passport]).

export function getDateDifferenceMs(a: DateInput, b: DateInput): number {
	return dayjs(a).diff(dayjs(b));
}
/** Age of `date` relative to now, in milliseconds (≥ 0 for past instants). */
export function ageInMs(date: DateInput): number {
	return getCurrentTimestampMs() - toTimestampMs(date);
}
/** Whether `date` is no older than `maxAgeMs` (the program-partner-supplied freshness window). */
export function isFresh(date: DateInput, maxAgeMs: number): boolean {
	return ageInMs(date) <= maxAgeMs;
}

// ── Formatting ────────────────────────────────────────────────────────────────
// API formatting is fixed/locale-independent. `formatWithPattern` renders in an
// explicit timezone (or UTC); passing `getLocalTimeZone()` renders the viewer's
// wall-clock time for client-side display.

export function formatDateForAPI(date: DateInput): string {
	return dayjs(date).format("YYYY-MM-DD");
}
export function formatDateTimeForAPI(date: DateInput): string {
	return dayjs(date).toISOString();
}
/** The runtime's IANA time zone (e.g. `Europe/Athens`) — the operator's machine zone in a browser. */
export function getLocalTimeZone(): string {
	return dayjs.tz.guess();
}
export function formatWithPattern(date: DateInput, pattern: string, timezone?: string): string {
	const d = dayjs(date);
	return timezone ? d.tz(timezone).format(pattern) : d.utc().format(pattern);
}
export function formatAsRelativeTime(date: DateInput, withSuffix = true): string {
	return dayjs(date).fromNow(!withSuffix);
}
